(ns app.front.view.bond.workspace-section
  (:require [app.domain.bond.model :as bond-model]
            [app.front.entities :as entities]
            [app.front.process :as process]
            [app.front.ui :as ui]
            [app.front.ui.accordion :as accordion]
            [app.front.ui.action-buttons :as action-buttons]
            [app.front.ui.modal :as modal]
            [app.front.ui.table :as table]
            [app.front.util :refer [for-frag] :as futil]
            [app.front.view.bond.common :as bond-common]
            [app.front.view.bond.subs :as bond-subs]
            [app.front.view.workspace.section :as ws-section]
            [app.front.view.workspace.subs :as ws-subs]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.href :as href]
            [core.front.i18n :as i18n]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

(rf/reg-sub ::workspace-bonds
  :<- [::ws-subs/workspace]
  (fn [workspace]
    (->> (:workspace/bonds workspace)
         (remove nil?))))

(rf/reg-event-fx ::fetch-bonds-available-to-associate
  (fn [_ [_ workspace-id]]
    {:dispatch [:comm/command-monitored
                {:command    :bond/get-bonds-available-to-associate
                 :method     :get
                 :data       {:workspace-id workspace-id}
                 :on-success [::entities/merge-entity-db]}]}))

(rf/reg-sub ::bonds-available-to-associate
  :<- [:session/user]
  :<- [::ws-subs/workspace]
  :-> (fn [[user workspace]]
        (bond-model/bonds-available-to-associate user workspace)))

(rf/reg-sub ::user-can-associate-a-bond?
  (fn [_ [_ fsm-target]]
    (<sub [::process/signal-permitted?
           fsm-target
           [:process.signal/associate-bond]])))

(rf/reg-sub ::user-can-disassociate-a-bond?
  :<- [::workspace-bonds]
  (fn [workspace-bonds [_ fsm-target]]
    (->> workspace-bonds
         (some (fn [bond]
                 (<sub [::process/signal-permitted?
                        fsm-target
                        [:process.signal/disassociate-bond
                         {:bond-id (:db/id bond)}]]))))))

(rf/reg-sub ::workspace-bonds-section-visible?
  :<- [::ff/enabled? :feature-flag.id/bonds]
  :<- [::workspace-bonds]
  (fn [[ff-enabled? workspace-bonds] [_ fsm-target]]
    (and ff-enabled?
         (or (util/not-empty? workspace-bonds)
             (<sub [::user-can-associate-a-bond? fsm-target])))))

(def bonds-accordion-id "bonds-accordion")

(rf/reg-event-db ::clear-bonds-accordion-state
  (fn [db _]
    (p/dissoc-in db [::accordion/section-states bonds-accordion-id])))

(rf/reg-event-db ::open-modal
  (fn [db [_ modal-type]]
    (assoc db ::modal-type modal-type)))

(rf/reg-event-db ::close-modal
  (fn [db [_ on-close]]
    (on-close)
    (dissoc db ::modal-type)))

(rf/reg-sub ::modal-type :-> ::modal-type)

(defn header-field->text [bond]
  {:bond-external-id (or (:bond/external-id bond) "-")
   :bond-type-name   (bond-model/bond-type-name bond (<sub [:language]))
   :bond-amount      (-> bond
                         :bond/amount
                         (futil/money-string
                           (-> bond :bond/group :group/currency)))
   :bond-expires-on  (->> bond
                          :bond/expires-on
                          (date-util/date
                            (<sub [::i18n/full-locale])))})

(def header-item
  :span.px-2.border-r.border-solid.border-ui-gray)

(defn- header [bond]
  (let [field->text (header-field->text bond)]
    [:div.flex
     [header-item
      (field->text :bond-external-id)]
     [header-item
      (field->text :bond-type-name)]
     [header-item
      (field->text :bond-amount)]
     [:span.px-2
      (field->text :bond-expires-on)]]))

(defn expandable-bond [bond on-click]
  (fn [{:keys [collapsed? expandable-container-props toggle-and-close-others-fn]}]
    (when bond
      [:div.rounded.cursor-pointer
       {:class    (if collapsed?
                    "bg-ui-light-gray"
                    "bg-ui-white border border-solid border-ui-violet border-2")
        :on-click (fn []
                    (on-click)
                    (toggle-and-close-others-fn))}
       [:div
        (expandable-container-props)
        [:div.px-1.py-3.rounded
         [header bond]
         [:div.mx-2.mt-2.pt-2.border-t.border-solid.border-ui-gray
          (-> bond :bond/description)]]]
       (when collapsed?
         [:div.py-2
          [header bond]])])))

(defn select-bond-modal [tr fsm-target {:keys [bonds loading? modal-type modal-title-tr-key
                                               notify-success-tr-key process-signal filter-fn]}]
  (r/with-let [*selected-bond-id (r/atom nil)
               *search-query     (r/atom "")]
    (let [selectable-bonds    (if filter-fn (filter filter-fn bonds) bonds)
          clear-selected-bond (fn []
                                (reset! *selected-bond-id nil)
                                (>evt [::clear-bonds-accordion-state]))
          close-modal-event   [::close-modal (fn []
                                               (clear-selected-bond)
                                               (reset! *search-query ""))]
          display-search-bar? (>= (count selectable-bonds) 10)]
      [modal/composed-input-modal
       {:open       (= (<sub [::modal-type]) modal-type)
        :on-close   #(>evt close-modal-event)
        :aria-label (tr [modal-title-tr-key])}
       (cc/modal-header {:close-modal #(>evt close-modal-event)
                         :title       (tr [modal-title-tr-key])})
       (cc/modal-body
         {}
         (cond
           loading?
           [:div.flex.justify-center.py-6
            (cc/loading-spinner {:with-overlay false})]

           (empty? selectable-bonds)
           [:p (tr [:bond/no-bonds-available])]

           :else
           (let [query          (-> @*search-query str/trim str/lower-case)
                 filtered-bonds (if (str/blank? query)
                                  selectable-bonds
                                  (filter (fn [bond]
                                            (let [header-text (->> (header-field->text bond)
                                                                   vals
                                                                   (map str/lower-case)
                                                                   (str/join " "))]
                                              (str/includes? header-text query)))
                                          selectable-bonds))]
             [:<>
              (when display-search-bar?
                [:div.mb-4
                 [ui/search
                  {:id                      "bonds-filter"
                   :label-text              (tr [:bond/search-label])
                   :close-button-label-text (tr [:search/empty])
                   :placeholder             (tr [:bond/search-placeholder])
                   :value                   @*search-query
                   :on-change               (fn [e]
                                              (reset! *search-query (futil/tval e))
                                              (clear-selected-bond))
                   :clear-fn                (fn []
                                              (reset! *search-query "")
                                              (clear-selected-bond))
                   :hide-label              true}]])
              [:div.overflow-y-auto.flex.flex-col.gap-2.mb-6
               (when display-search-bar?
                 {:style {:height "300px"}})
               (if (empty? filtered-bonds)
                 [:p (tr [:bond/search-no-results])]
                 [accordion/accordion
                  bonds-accordion-id
                  {:transition-duration 240
                   :transition-function "ease"}
                  (for-frag [bond (sort-by :entity/created filtered-bonds)
                             :let [bond-id (:db/id bond)]]
                    ^{:key bond-id}
                    (expandable-bond bond #(reset! *selected-bond-id
                                                   (when-not (= @*selected-bond-id bond-id)
                                                     bond-id))))])]
              [action-buttons/save-cancel-buttons
               (util/mmap {:on-cancel      #(>evt close-modal-event)
                           :cancel-tr      :bond/modal-cancel
                           :on-save        #(>evt [::process/signal
                                                   fsm-target
                                                   [process-signal {:bond-id @*selected-bond-id}]
                                                   {:notify  {:success notify-success-tr-key}
                                                    :on-done close-modal-event}])
                           :save-tr        :bond/modal-done
                           :save-disabled? (nil? @*selected-bond-id)
                           :loading?       (<sub [::process/signal-processing? process-signal])
                           :size           :field}
                          tr)]])))])))

(defn associate-bond-modal [tr fsm-target]
  [select-bond-modal
   tr
   fsm-target
   {:bonds                 (<sub [::bonds-available-to-associate])
    :loading?              (<sub [:comm/command-processing? :bond/get-bonds-available-to-associate])
    :modal-type            :associate
    :modal-title-tr-key    :bond/associate-modal-title
    :notify-success-tr-key :bond/associated
    :process-signal        :process.signal/associate-bond}])

(defn disassociate-bond-modal [tr fsm-target]
  [select-bond-modal
   tr
   fsm-target
   {:bonds                 (<sub [::workspace-bonds])
    :modal-type            :disassociate
    :modal-title-tr-key    :bond/disassociate-modal-title
    :notify-success-tr-key :bond/disassociated
    :process-signal        :process.signal/disassociate-bond
    :filter-fn             (fn [bond]
                             (<sub [::process/signal-permitted?
                                    fsm-target
                                    [:process.signal/disassociate-bond
                                     {:bond-id (:db/id bond)}]]))}])

(defn associate-bond-button [tr fsm-target]
  (when (<sub [::user-can-associate-a-bond? fsm-target])
    [ui/button
     {:kind     :secondary
      :size     :field
      :on-click (fn []
                  (>evt [::fetch-bonds-available-to-associate
                         (-> fsm-target :entity/parent :workspace/id)])
                  (>evt [::open-modal :associate]))}
     (tr [:bond/associate])]))

(defn disassociate-bond-button [tr fsm-target]
  (when (<sub [::user-can-disassociate-a-bond? fsm-target])
    (let [workspace-bonds (<sub [::workspace-bonds])
          loading?        (<sub [::process/signal-processing? :process.signal/disassociate-bond])]
      [ui/button
       {:kind     :secondary
        :size     :field
        :disabled loading?
        :on-click (fn []
                    (if (= 1 (count workspace-bonds))
                      (>evt [::process/signal
                             fsm-target
                             [:process.signal/disassociate-bond
                              {:bond-id (-> workspace-bonds first :db/id)}]
                             {:notify {:success :bond/disassociated}}])
                      (>evt [::open-modal :disassociate])))}
       (tr [:bond/disassociate])
       (when loading?
         [:div.ml-2
          (cc/loading-spinner {:with-overlay false :small true})])])))

(defn create-bond-button [tr fsm-target wizard?]
  (when (<sub [::user-can-associate-a-bond? fsm-target])
    (let [workspace (-> fsm-target :entity/parent)]
      [ui/button
       {:kind :secondary
        :size :field
        :href (href/href :view/bond.new
                         {}
                         (cond-> {:workspace (:workspace/id workspace)}
                           wizard? (assoc :wizard true)))}
       (tr [:bond/create-application])])))

(rf/reg-event-db ::open-verifying-bond
  (fn [db [_ bond]]
    (assoc db ::verifying-bond bond)))

(rf/reg-event-db ::close-verifying-bond
  (fn [db _]
    (dissoc db ::verifying-bond)))

(rf/reg-sub ::verifying-bond :-> ::verifying-bond)

(rf/reg-event-fx ::verify-bond.success
  (fn [_ [_ bond-id]]
    {:fx [[:dispatch [::close-verifying-bond]]
          [:dispatch [::bond-subs/delete-bond-verification-task bond-id]]]}))

(defn- verify-bond-button [bond tr fsm-target]
  (when (<sub [::process/signal-permitted?
               fsm-target
               [:process.signal/verify-bond {:bond-id (:db/id bond)}]])
    [ui/button
     {:kind     :primary
      :disabled (not (<sub [::bond-common/bond-has-all-required-attachments? (:db/id bond)]))
      :on-click #(>evt [::open-verifying-bond bond])}
     (tr [:bond/verify-bond])]))

(defn- bond-expanded-content [tr fsm-target bond]
  [:div.flex.flex-col.gap-4
   (when-let [description (:bond/description bond)]
     [:div
      [:div.font-bold.text-sm.mb-1 (tr [:bond/details])]
      [:div description]])
   [verify-bond-button bond tr fsm-target]
   (when (= bond (<sub [::verifying-bond]))
     [bond-common/verify-bond-modal
      (util/mmap
        {:close-modal-event [::close-verifying-bond]
         :verify-bond       #(>evt [::process/signal
                                    fsm-target
                                    [:process.signal/verify-bond %]
                                    {:notify     {:success :bond/verified-success}
                                     :on-success [::verify-bond.success (:db/id bond)]}])
         :verifying?        (<sub [::process/signal-processing? :process.signal/verify-bond])}
        bond
        tr)])])

(defn- bonds-table [tr fsm-target workspace-bonds wizard?]
  (let [lang         (<sub [:language])
        full-locale  (<sub [::i18n/full-locale])
        workspace-id (-> fsm-target :entity/parent :workspace/id)]
    [table/modern-responsive-table
     {:cols          [[:bond/account-number #(or (:bond/external-id %) "-")]
                      [:bond/issuer-name :bond/issuer-name]
                      [:bond/type #(bond-model/bond-type-name % lang)]
                      [:bond/amount #(futil/money-string
                                       (:bond/amount %)
                                       (-> % :bond/group :group/currency))]
                      [:bond/expires-on #(date-util/date full-locale (:bond/expires-on %))]
                      [:bond/status #(tr [(-> % :domain/state)])]]
      :items         workspace-bonds
      :tr            tr
      :get-status    bond-common/bond-status-colors
      :id-func       :db/id
      :link          {:description :bond/view
                      :make-href   #(href/href :view/bond.base
                                               {:bond-id (:bond/id %)}
                                               (when wizard?
                                                 {:wizard    true
                                                  :workspace workspace-id}))}
      :expanded-func (partial bond-expanded-content tr fsm-target)}]))

(defn bonds-content [{:keys [tr fsm-target wizard?]}]
  (let [workspace-bonds (<sub [::workspace-bonds])]
    [:<>
     [:div.flex.flex-wrap.gap-4.mb-4
      [disassociate-bond-button tr fsm-target]
      [associate-bond-button tr fsm-target]
      [create-bond-button tr fsm-target wizard?]]
     (when (util/not-empty? workspace-bonds)
       [bonds-table tr fsm-target workspace-bonds wizard?])
     [associate-bond-modal tr fsm-target]
     [disassociate-bond-modal tr fsm-target]]))

(defn workspace-bonds-section [{:keys [tr fsm-target]}]
  (when (<sub [::workspace-bonds-section-visible? fsm-target])
    (let [workspace       (<sub [::ws-subs/workspace])
          workspace-bonds (<sub [::workspace-bonds])
          all-verified?   (and (util/not-empty? workspace-bonds)
                               (every? #(= :bond.state/verified (:domain/state %)) workspace-bonds))]
      [ws-section/section
       workspace
       :workspace-bonds
       (util/mmap {:heading-tag       (count workspace-bonds)
                   :heading-tag-color (when (bond-model/bond-required-for-workspace? workspace)
                                        (if all-verified?
                                          :green
                                          :yellow))}
                  tr)
       :workspace/bonds
       true
       [bonds-content {:tr tr :fsm-target fsm-target}]])))
